import {
  Avatar, Box, Button, Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle, IconButton, Menu, MenuItem,
  Stack, TextField, Typography,
} from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import SendMessage from "./SendMessage";
import RichContentViewer from "../../../Shared/RichContentViewer";
import {
  createQuestion,
  getQuestionsInRealTimeById,
  updateQuestion,
  deleteQuestion,
} from "../../../Config/db/questions.retrieve";
import { ProjectContext } from "../../../AppContext/ProjectContext";
import moment from "moment";
import { Timestamp } from "firebase/firestore";
import { AuthContext } from "../../../AppContext/AuthContext";
import Answers from "./Answers";
import { createAnswer } from "../../../Config/db/answers.retrieve";
import { extractPlainText } from "../helper";
import { MoreVert, Edit, Delete, Reply as ReplyIcon } from "@mui/icons-material";

export default function Questions() {
  const [messages, setMessages] = useState([]);
  const { projectInfo, participants } = useContext(ProjectContext);
  const { currentUser } = useContext(AuthContext);

  // Edit state
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");

  // Menu state
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuItemId, setMenuItemId] = useState(null);

  // Delete confirmation
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState(null);

  useEffect(() => {
    if (projectInfo?.id) {
      const unSub = getQuestionsInRealTimeById(projectInfo.id, (data) => {
        setMessages(data);
      });
      return () => {
        unSub();
      };
    }
  }, [projectInfo?.id]);

  const handleSend = (message, senderId) => {
    if (projectInfo) {
      createQuestion(projectInfo.id, message, senderId);
    }
  };

  const handleReply = (questionId, message, senderId) => {
    createAnswer(questionId, message, senderId);
  };

  const handleMenuOpen = (e, id) => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
    setMenuItemId(id);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuItemId(null);
  };

  const handleEditStart = (item) => {
    setEditingId(item.id);
    setEditText(extractPlainText(item.text));
    handleMenuClose();
  };

  const handleEditSave = async () => {
    if (editingId && editText.trim()) {
      await updateQuestion(editingId, editText.trim());
    }
    setEditingId(null);
    setEditText("");
  };

  const handleEditCancel = () => {
    setEditingId(null);
    setEditText("");
  };

  const handleDeleteClick = (id) => {
    setDeleteTargetId(id);
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleDeleteConfirm = async () => {
    if (deleteTargetId) {
      await deleteQuestion(deleteTargetId);
    }
    setDeleteDialogOpen(false);
    setDeleteTargetId(null);
  };

  return (
    <Stack spacing={0}>
      <SendMessage title="Add Comment" handleSend={handleSend} />
      {messages?.map((each) => {
        const date = new Timestamp(
          each.timestamp.seconds,
          each.timestamp.nanoseconds
        ).toDate();
        const isOwner = each.senderId === currentUser.uid;
        const authorName = isOwner
          ? currentUser.displayName
          : participants?.[each.senderId]?.displayName || "Unknown";
        const photoURL = isOwner
          ? currentUser.photoURL
          : participants?.[each.senderId]?.photoURL;

        return (
          <Box key={each.id} sx={{ py: 1.25, "&:not(:last-child)": { borderBottom: "1px solid #f0f0f0" } }}>
            {/* Header row: avatar + name + time + menu */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.25 }}>
              <Avatar src={photoURL} sx={{ width: 24, height: 24, fontSize: "0.7rem" }}>
                {authorName?.[0] || "?"}
              </Avatar>
              <Typography variant="body2" fontWeight={600} sx={{ fontSize: "0.82rem" }}>
                {authorName}
              </Typography>
              <Typography variant="caption" color="text.disabled" sx={{ fontSize: "0.7rem" }}>
                {moment(date).fromNow()}
              </Typography>
              {each.editedAt && (
                <Typography variant="caption" color="text.disabled" sx={{ fontSize: "0.65rem" }}>
                  (edited)
                </Typography>
              )}
              <Box sx={{ flex: 1 }} />
              {isOwner && (
                <IconButton size="small" onClick={(e) => handleMenuOpen(e, each.id)} sx={{ p: 0.25 }}>
                  <MoreVert sx={{ fontSize: 16 }} />
                </IconButton>
              )}
            </Box>

            {/* Content */}
            {editingId === each.id ? (
              <Box sx={{ ml: 4 }}>
                <TextField
                  multiline
                  minRows={1}
                  maxRows={6}
                  fullWidth
                  value={editText}
                  onChange={(e) => setEditText(e.target.value)}
                  variant="outlined"
                  size="small"
                  placeholder="Edit comment…"
                  autoFocus
                  sx={{ mb: 0.5 }}
                />
                <Stack direction="row" spacing={1}>
                  <Button size="small" variant="outlined" onClick={handleEditCancel} sx={{ textTransform: "none", borderRadius: 2, fontSize: "0.75rem", py: 0.25 }}>
                    Cancel
                  </Button>
                  <Button size="small" variant="contained" onClick={handleEditSave} disabled={!editText.trim()} sx={{ textTransform: "none", borderRadius: 2, fontSize: "0.75rem", py: 0.25, background: "linear-gradient(135deg, #1a237e 0%, #283593 100%)" }}>
                    Save
                  </Button>
                </Stack>
              </Box>
            ) : (
              <Box sx={{ ml: 4 }}>
                <RichContentViewer content={each.text} />
                <Box sx={{ display: "flex", gap: 1, mt: 0.25 }}>
                  <Button
                    size="small"
                    startIcon={<ReplyIcon sx={{ fontSize: 14 }} />}
                    onClick={() => {
                      const trigger = document.getElementById(`reply-${each.id}`);
                      if (trigger) trigger.click();
                    }}
                    sx={{ textTransform: "none", fontSize: "0.75rem", color: "text.secondary", minWidth: "auto", p: "2px 6px" }}
                  >
                    Reply
                  </Button>
                </Box>
              </Box>
            )}

            {/* Replies */}
            <Box sx={{ ml: 4 }}>
              <Answers
                questionId={each.id}
                handleReply={(message, senderId) => handleReply(each.id, message, senderId)}
                replyTriggerId={`reply-${each.id}`}
              />
            </Box>
          </Box>
        );
      })}

      {/* Context menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem onClick={() => handleEditStart(messages.find((m) => m.id === menuItemId))} dense>
          <Edit fontSize="small" sx={{ mr: 1 }} /> Edit
        </MenuItem>
        <MenuItem onClick={() => handleDeleteClick(menuItemId)} dense sx={{ color: "error.main" }}>
          <Delete fontSize="small" sx={{ mr: 1 }} /> Delete
        </MenuItem>
      </Menu>

      {/* Delete confirmation dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Comment</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this comment? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} sx={{ textTransform: "none" }}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" sx={{ textTransform: "none" }}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
